<script setup lang="ts">
import { computed, ref } from 'vue';
import FormSection from './FormSection.vue';
import ResumeDropzone from '@/components/Shared/ResumeUpload/ResumeDropzone.vue';
import ResumeFileList from '@/components/Shared/ResumeUpload/ResumeFileList.vue';
import EngagementModeSelector from '@/components/Shared/ResumeUpload/EngagementModeSelector.vue';
import SendCandidatesHereSection from '@/components/Shared/ResumeUpload/SendCandidatesHereSection.vue';
import { useResumeUpload } from '@/composables/useResumeUpload';
import type { EngagementMode } from '@/core/sourcing/outreach/outreach.persistence';
import type { ResumeUploadFile } from '@/core/sourcing/outreach/outreach.service';

const props = defineProps<{
  jobId: number | null;
  activeOutreachEnabled: boolean;
}>();

const { selectedFiles, selectedOption, addFiles, removeFile, clearAllFiles, toBase64 } =
  useResumeUpload();

const isUploading = ref(false);

const isContactModeEnabled = computed(() => props.activeOutreachEnabled);

const hasStagedFiles = computed(() => selectedFiles.value.length > 0);

/**
 * Returns the staged files with base64 data for deferred upload.
 * Called by the parent after successful activation.
 */
async function getStagedFilesForUpload(): Promise<{
  files: ResumeUploadFile[];
  engagementMode: EngagementMode;
} | null> {
  if (selectedFiles.value.length === 0) return null;

  isUploading.value = true;
  try {
    const filesWithBase64: ResumeUploadFile[] = await Promise.all(
      selectedFiles.value.map(async (file) => {
        const base64Data = await toBase64(file);
        return { file, base64Data };
      }),
    );
    return { files: filesWithBase64, engagementMode: selectedOption.value };
  } finally {
    isUploading.value = false;
  }
}

defineExpose({
  hasStagedFiles,
  getStagedFilesForUpload,
  isUploading,
});
</script>

<template>
  <FormSection
    id="upload-candidates"
    title="Upload candidates"
    subtitle="Add external candidates to this job by uploading their resumes."
    :add-divider="false"
  >
    <div class="flex flex-col gap-6">
      <SendCandidatesHereSection v-if="props.jobId" :job-id="props.jobId" />

      <ResumeDropzone @files-selected="addFiles" />

      <ResumeFileList :files="selectedFiles" @remove-file="removeFile" @clear-all="clearAllFiles" />

      <EngagementModeSelector
        :model-value="selectedOption"
        :is-contact-mode-enabled="isContactModeEnabled"
        @update:model-value="selectedOption = $event"
      />
    </div>
  </FormSection>
</template>
